import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Gmap from './Gmap';
import Select from 'react-select';
import Slider from '@mui/material/Slider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {
  carModelMakes as carMakeOptions,
  motorcycleModelMakes as motorMakeOptions,
  colorOptions,
  transOptions,
  driveOptions,
  engineOptions,
  bodyOptions,
  fuelOptions,
  premiumFeaturesOptions,
  carModelMakesJson,
} from '../contants/data';
// ICONS
import { GrPowerReset } from 'react-icons/gr';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';

const Filters = ({ onFilter, data, stateCategory, filters }) => {
  const [thunks, setThunks] = useState(null);
  const [showAdvFilter, setShowAdvFilter] = useState(false);
  const { isFilter } = useSelector((state) => state[stateCategory]);
  const dispatch = useDispatch();

  // GEOLOCATE
  const { lat, lng, radius } = useSelector(
    (state) => state[stateCategory].filters.location
  );

  // NEW LAT,LNG
  const [newLat, setNewLat] = useState('');
  const [newLng, setNewLng] = useState('');

  // MAKE, MODEL
  const { make, model } = useSelector((state) => state[stateCategory].filters);
  const [modelOptions, setModelOptions] = useState([]);

  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  const makeOptionsFromJson = useMemo(
    () =>
      (carModelMakesJson || []).map((m) => ({ label: m.make, value: m.make })),
    []
  );

  // NEW: helper to get model options: model label, value = children JSON
  const getModelOptions = (makeVal) =>
    (carModelMakesJson.find((m) => m.make === makeVal)?.models || []).map(
      (m) => {
        const kids = Array.isArray(m.children) ? m.children : [];
        const tokens = [...new Set([m.model, ...kids])];
        return { label: m.model, value: tokens.join(',') };
      }
    );

  const handleMakeChange = (option) => {
    setSelectedMake(() => option);
    dispatch(thunks?.changeMake(option.value));

    // reset model array
    setSelectedModel([]);
    dispatch(thunks?.changeModel(''));

    if (stateCategory === 'motorcycles') {
      setModelOptions(
        () => motorMakeOptions.find((make) => make.label === option.value).model
      );
    }
    if (stateCategory === 'cars' || stateCategory === 'classicCollectorCars') {
      setModelOptions(() => getModelOptions(option.value));
    }
  };

  const handleModelChange = (option) => {
    setSelectedModel(() => option);
    dispatch(thunks?.changeModel(option.value || option.label));
  };

  // PRICE
  const { min: priceMin, max: priceMax } = useSelector(
    (state) => state[stateCategory].filters.price_range
  );
  const [priceVal, setPriceVal] = useState([priceMin, priceMax]);

  const handlePriceChange = (event, newValue) => {
    setPriceVal(newValue);
    dispatch(thunks?.changePrice({ min: newValue[0], max: newValue[1] }));
  };

  const handleMinPriceChange = (e) => {
    const newMin = Number(e.target.value);
    setPriceVal([newMin, priceVal[1]]);
    dispatch(thunks?.changePrice({ min: newMin, max: priceVal[1] }));
  };

  const handleMaxPriceChange = (e) => {
    const newMax = Number(e.target.value);
    setPriceVal([priceVal[0], newMax]);
    dispatch(thunks?.changePrice({ min: priceVal[0], max: newMax }));
  };

  // YEAR
  const { min: yearMin, max: yearMax } = useSelector(
    (state) => state[stateCategory].filters.year_range
  );
  const [yearVal, setYearVal] = useState([yearMin, yearMax]);
  const handleYearChange = (event, newValue) => {
    setYearVal(newValue);
    dispatch(thunks?.changeYear({ min: newValue[0], max: newValue[1] }));
  };

  const handleMinYearChange = (e) => {
    const newMin = Number(e.target.value);
    setYearVal([newMin, yearVal[1]]);
    dispatch(thunks?.changeYear({ min: newMin, max: yearVal[1] }));
  };

  const handleMaxYearChange = (e) => {
    const newMax = Number(e.target.value);
    setYearVal([yearVal[0], newMax]);
    dispatch(thunks?.changeYear({ min: yearVal[0], max: newMax }));
  };

  // MILES
  const { min: milesMin, max: milesMax } = useSelector(
    (state) => state[stateCategory].filters.mile_range
  );
  const [milesVal, setMilesVal] = useState([milesMin, milesMax]);
  const handleMilesChange = (event, newValue) => {
    setMilesVal(newValue);
    dispatch(thunks?.changeMile({ min: newValue[0], max: newValue[1] }));
  };

  // HOURS
  const { min: hoursMin, max: hoursMax } = useSelector(
    (state) => state[stateCategory].filters.hour_range
  );
  const [hoursVal, setHoursVal] = useState([hoursMin, hoursMax]);
  const handleHoursChange = (event, newValue) => {
    setHoursVal(newValue);
    dispatch(thunks?.changeHours({ min: newValue[0], max: newValue[1] }));
  };

  // HORSEPOWER
  const { min: horsepowerMin, max: horsepowerMax } = useSelector(
    (state) => state[stateCategory].filters.horsepower_range
  );
  const [horsepowerVal, setHorsepowerVal] = useState([
    horsepowerMin,
    horsepowerMax,
  ]);
  const handleHorsepowerChange = (event, newValue) => {
    setHorsepowerVal(newValue);
    dispatch(thunks?.changeHorsepower({ min: newValue[0], max: newValue[1] }));
  };

  // ENGINE SIZE
  const { min: engineSizeMin, max: engineSizeMax } = useSelector(
    (state) => state[stateCategory].filters.sizeEngine_range
  );
  const [engineSizeVal, setEngineSizeVal] = useState([
    engineSizeMin,
    engineSizeMax,
  ]);
  const handleEngineSizeChange = (event, newValue) => {
    setEngineSizeVal(newValue);
    dispatch(thunks?.changeSizeEngine({ min: newValue[0], max: newValue[1] }));
  };

  // LENGTH
  const { min: lengthMin, max: lengthMax } = useSelector(
    (state) => state[stateCategory].filters.length_range
  );
  const [lengthVal, setLengthVal] = useState([lengthMin, lengthMax]);
  const handleLengthChange = (event, newValue) => {
    setLengthVal(newValue);
    dispatch(thunks?.changeLength({ min: newValue[0], max: newValue[1] }));
  };

  // CONDITION
  const { condition } = useSelector((state) => state[stateCategory].filters);

  // Exterior Color
  const { extColors } = useSelector((state) => state[stateCategory].filters);
  const [selectedExtColors, setSelectedExtColors] = useState([]);
  const handleExtColorChange = (options) => {
    setSelectedExtColors(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeExtColors(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // Interior Color
  const { intColors } = useSelector((state) => state[stateCategory].filters);
  const [selectedIntColors, setSelectedIntColors] = useState([]);
  const handleIntColorChange = (options) => {
    setSelectedIntColors(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeIntColors(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // Transmission
  const { transmission } = useSelector((state) => state[stateCategory].filters);
  const [selectedTrans, setSelectedTrans] = useState([]);
  const handleTransChange = (options) => {
    setSelectedTrans(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeTrans(options ? options.map((option) => option.value) : [])
    );
  };

  // Drive
  const { drive } = useSelector((state) => state[stateCategory].filters);
  const [selectedDrive, setSelectedDrive] = useState([]);
  const handleDriveChange = (options) => {
    setSelectedDrive(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeDrive(options ? options.map((option) => option.value) : [])
    );
  };

  // Engine
  const { engine } = useSelector((state) => state[stateCategory].filters);
  const [selectedEngine, setSelectedEngine] = useState([]);
  const handleEngineChange = (options) => {
    setSelectedEngine(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeEngine(options ? options.map((option) => option.value) : [])
    );
  };

  // Body
  const { body } = useSelector((state) => state[stateCategory].filters);
  const [selectedBody, setSelectedBody] = useState([]);
  const handleBodyChange = (options) => {
    setSelectedBody(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeBody(options ? options.map((option) => option.value) : [])
    );
  };

  // FUEL
  const { fuel } = useSelector((state) => state[stateCategory].filters);
  const [selectedFuel, setSelectedFuel] = useState([]);
  const handleFuelChange = (options) => {
    setSelectedFuel(options ? options.map((option) => option.value) : []);
    dispatch(
      thunks?.changeFuel(options ? options.map((option) => option.value) : [])
    );
  };

  // PREMIUM FEATURES
  const { premium_features } = useSelector(
    (state) => state[stateCategory].filters
  );
  const [selectedPremFeatures, setSelectedPremFeatures] = useState([]);
  const handlePremFeaturesChange = (options) => {
    setSelectedPremFeatures(
      options ? options.map((option) => option.value) : []
    );
    dispatch(
      thunks?.changePremiumFeatures(
        options ? options.map((option) => option.value) : []
      )
    );
  };

  // SELLER
  const { seller } = useSelector((state) => state[stateCategory].filters);

  // TITLE STATUS
  const { cleanTitle } = useSelector((state) => state[stateCategory].filters);

  useEffect(() => {
    const loadThunks = async () => {
      let importedThunks;
      if (stateCategory === 'cars') {
        importedThunks = await import('../store/slices/carsTrucks.slice');
      } else if (stateCategory === 'atvsUtvs') {
        importedThunks = await import('../store/slices/atvsUtvs.slice');
      } else if (stateCategory === 'snowmobiles') {
        importedThunks = await import('../store/slices/snowmobiles.slice');
      } else if (stateCategory === 'watercrafts') {
        importedThunks = await import('../store/slices/watercrafts.slice');
      } else if (stateCategory === 'classicCollectorCars') {
        importedThunks = await import(
          '../store/slices/classicCollectorCars.slice'
        );
      } else if (stateCategory === 'motorcycles') {
        importedThunks = await import('../store/slices/motorcycles.slice');
      } else if (stateCategory === 'rvs') {
        importedThunks = await import('../store/slices/rvs.slice');
      } else if (stateCategory === 'heavyEquipments') {
        importedThunks = await import('../store/slices/heavyEquipments.slice');
      }

      setThunks(importedThunks);
    };

    loadThunks();
  }, [stateCategory]);

  if (!thunks) {
    return <div>Loading...</div>;
  }

  const handleSubmit = () => {
    let finalLat = '';
    let finalLng = '';
    if ((newLat != '') & (newLng != '')) {
      finalLat = newLat;
      finalLng = newLng;
      dispatch(thunks?.changeLocation({ lat: finalLat, lng: finalLng }));
    } else {
      finalLat = lat;
      finalLng = lng;
    }

    onFilter({
      finalLat,
      finalLng,
      radius,
      make,
      model,
      priceVal,
      yearVal,
      milesVal,
      extColors,
      intColors,
      transmission,
      drive,
      engine,
      body,
      fuel,
      premium_features,
      seller,
      cleanTitle,
    });
    dispatch(thunks?.activateFilter(true));
    dispatch(thunks?.setSearchTerm(''));
    dispatch(thunks?.setSortBy('Closest to Me'));
    dispatch(thunks?.resetSortedData());
  };

  const updateLoc = (lat, lng) => {
    setNewLat(() => {
      return lat;
    });
    setNewLng(() => {
      return lng;
    });
  };

  const handleResetFilter = () => {
    window.location.reload();
  };

  const handleResetMake = () => {
    setSelectedMake(() => null);
    setSelectedModel(() => null);
    setModelOptions(() => []);
    dispatch(thunks?.changeMake(''));
    dispatch(thunks?.changeModel(''));
  };

  const handleShowAdvFilter = () => {
    setShowAdvFilter(!showAdvFilter);
  };

  return (
    <div className='sidebar hide-mobile'>
      <Gmap onChangeLoc={updateLoc} data={data} />
      {filters.includes('Make') && (
        <div className='form-control-wrap'>
          <div className='label-2-cols'>
            <label>Make</label>
            {selectedMake !== null && (
              <div className='reset-select' onClick={handleResetMake}>
                <GrPowerReset />
              </div>
            )}
          </div>
          <Select
            options={
              stateCategory === 'motorcycles'
                ? motorMakeOptions
                : makeOptionsFromJson
            }
            onChange={handleMakeChange}
            value={selectedMake}
            className='border-0'
          />
        </div>
      )}
      {filters.includes('Model') && (
        <div className='form-control-wrap'>
          <label>Model</label>
          <Select
            options={modelOptions}
            onChange={handleModelChange}
            value={selectedModel}
            isDisabled={modelOptions.length === 0}
            className='border-0'
          />
        </div>
      )}
      {filters.includes('Condition') && (
        <div className='form-control-wrap'>
          <label>Condition</label>
          <FormControlLabel
            control={<Switch />}
            label='New Vehicles Only'
            onChange={() => {
              if (condition == null) {
                dispatch(thunks?.changeCondition(true));
                return;
              } else {
                dispatch(thunks?.changeCondition(!condition));
                return;
              }
            }}
          />
        </div>
      )}
      {filters.includes('Price') && (
        <div className='form-control-wrap'>
          <label>Price</label>
          <div className='range-inputs'>
            <input
              type='number'
              value={priceVal[0]}
              min={0}
              onChange={handleMinPriceChange}
              placeholder='Min'
            />
            <span className='price-separator'>–</span>
            <input
              type='number'
              value={priceVal[1]}
              min={0}
              onChange={handleMaxPriceChange}
              placeholder='Max'
            />
          </div>
          {/* <div className='price-range-wrap'>
            <span>${priceVal[0].toLocaleString('en-US')} - </span>
            <span>${priceVal[1].toLocaleString('en-US')}</span>
          </div> */}
        </div>
      )}
      {filters.includes('Year') && (
        <div className='form-control-wrap'>
          <label>Year</label>
          <div className='range-inputs'>
            <input
              type='number'
              value={yearVal[0]}
              min={1900}
              onChange={handleMinYearChange}
              placeholder='Min'
            />
            <span className='range-separator'>–</span>
            <input
              type='number'
              value={yearVal[1]}
              max={2025}
              onChange={handleMaxYearChange}
              placeholder='Max'
            />
          </div>
          {/* <div className='price-range-wrap'>
            <span>{yearVal[0]} - </span>
            <span>{yearVal[1]}</span>
          </div> */}
        </div>
      )}
      {filters.includes('Year Classic') && (
        <div className='form-control-wrap'>
          <label>Year</label>
          <Slider
            value={yearVal}
            onChange={handleYearChange}
            valueLabelDisplay='auto'
            min={1900}
            max={1990}
            step={1}
          />
          <div className='price-range-wrap'>
            <span>{yearVal[0]} - </span>
            <span>{yearVal[1]}</span>
          </div>
        </div>
      )}
      {filters.includes('Mileage') && (
        <div className='form-control-wrap'>
          <label>Mileage</label>
          <div className='range-inputs'>
            <input
              type='number'
              value={milesVal[0]}
              min={0}
              onChange={(e) => {
                const newMin = Number(e.target.value);
                setMilesVal([newMin, milesVal[1]]);
                dispatch(thunks?.changeMile({ min: newMin, max: milesVal[1] }));
              }}
              placeholder='Min'
            />
            <span className='range-separator'>–</span>
            <input
              type='number'
              value={milesVal[1]}
              min={milesVal[0]}
              onChange={(e) => {
                const newMax = Number(e.target.value);
                setMilesVal([milesVal[0], newMax]);
                dispatch(thunks?.changeMile({ min: milesVal[0], max: newMax }));
              }}
              placeholder='Max'
            />
          </div>
          {/* <div className='price-range-wrap'>
            <span>{milesVal[0].toLocaleString('en-US')} - </span>
            <span>{milesVal[1].toLocaleString('en-US')}</span>
          </div> */}
        </div>
      )}
      {filters.includes('Hours') && (
        <div className='form-control-wrap'>
          <label>Hours</label>
          <Slider
            value={hoursVal}
            onChange={handleHoursChange}
            valueLabelDisplay='auto'
            max={100}
            step={1}
          />
          <div className='price-range-wrap'>
            <span>{hoursVal[0].toLocaleString('en-US')} - </span>
            <span>{hoursVal[1].toLocaleString('en-US')}</span>
          </div>
        </div>
      )}
      <div id='advanceFilter'>
        <div className='header' onClick={handleShowAdvFilter}>
          <span>Additional Search Filters</span>
          <span className='action-button'>
            {showAdvFilter ? <FaMinusCircle /> : <FaPlusCircle />}
          </span>
        </div>
        {showAdvFilter && (
          <div className='advance-filters-wrap'>
            {filters.includes('Horsepower') && (
              <div className='form-control-wrap'>
                <label>Horsepower</label>
                <Slider
                  value={horsepowerVal}
                  onChange={handleHorsepowerChange}
                  valueLabelDisplay='auto'
                  min={0}
                  max={200}
                  step={1}
                />
                <div className='price-range-wrap'>
                  <span>{horsepowerVal[0]} - </span>
                  <span>{horsepowerVal[1]}</span>
                </div>
              </div>
            )}
            {filters.includes('Engine Size') && (
              <div className='form-control-wrap'>
                <label>Engine Size</label>
                <Slider
                  value={engineSizeVal}
                  onChange={handleEngineSizeChange}
                  valueLabelDisplay='auto'
                  min={0}
                  max={1000}
                  step={50}
                />
                <div className='price-range-wrap'>
                  <span>{engineSizeVal[0]} ccs - </span>
                  <span>{engineSizeVal[1]} ccs</span>
                </div>
              </div>
            )}
            {filters.includes('Length') && (
              <div className='form-control-wrap'>
                <label>Length</label>
                <Slider
                  value={lengthVal}
                  onChange={handleLengthChange}
                  valueLabelDisplay='auto'
                  min={0}
                  max={500}
                  step={1}
                />
                <div className='price-range-wrap'>
                  <span>{lengthVal[0]} ft - </span>
                  <span>{lengthVal[1]} ft</span>
                </div>
              </div>
            )}

            {filters.includes('Exterior Color') && (
              <div className='form-control-wrap'>
                <label>Exterior Color</label>
                <Select
                  isMulti
                  options={colorOptions}
                  onChange={handleExtColorChange}
                  value={colorOptions.filter((option) =>
                    selectedExtColors.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Interior Color') && (
              <div className='form-control-wrap'>
                <label>Interior Color</label>
                <Select
                  isMulti
                  options={colorOptions}
                  onChange={handleIntColorChange}
                  value={colorOptions.filter((option) =>
                    selectedIntColors.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Transmission') && (
              <div className='form-control-wrap'>
                <label>Transmission</label>
                <Select
                  isMulti
                  options={transOptions}
                  onChange={handleTransChange}
                  value={transOptions.filter((option) =>
                    selectedTrans.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Drive Type') && (
              <div className='form-control-wrap'>
                <label>Drive Type</label>
                <Select
                  isMulti
                  options={driveOptions}
                  onChange={handleDriveChange}
                  value={driveOptions.filter((option) =>
                    selectedDrive.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Engine Type') && (
              <div className='form-control-wrap'>
                <label>Engine Type</label>
                <Select
                  isMulti
                  options={engineOptions}
                  onChange={handleEngineChange}
                  value={engineOptions.filter((option) =>
                    selectedEngine.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Body Type') && (
              <div className='form-control-wrap'>
                <label>Body Type</label>
                <Select
                  isMulti
                  options={bodyOptions}
                  onChange={handleBodyChange}
                  value={bodyOptions.filter((option) =>
                    selectedBody.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Fuel Type') && (
              <div className='form-control-wrap'>
                <label>Fuel Type</label>
                <Select
                  isMulti
                  options={fuelOptions}
                  onChange={handleFuelChange}
                  value={fuelOptions.filter((option) =>
                    selectedFuel.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Premium Features') && (
              <div className='form-control-wrap'>
                <label>Premium Features</label>
                <Select
                  isMulti
                  options={premiumFeaturesOptions}
                  onChange={handlePremFeaturesChange}
                  value={premiumFeaturesOptions.filter((option) =>
                    selectedPremFeatures.includes(option.value)
                  )}
                  className='border-0'
                />
              </div>
            )}
            {filters.includes('Seller Type') && (
              <div className='form-control-wrap'>
                <label>Seller Type</label>
                <FormControlLabel
                  control={<Switch />}
                  label='Individuals Only'
                  onChange={() => {
                    if (seller == null) {
                      dispatch(thunks?.changeSeller(true));
                      return;
                    } else {
                      dispatch(thunks?.changeSeller(!seller));
                      return;
                    }
                  }}
                />
              </div>
            )}
            {filters.includes('Title Status') && (
              <div className='form-control-wrap'>
                <label>Title Status</label>
                <FormControlLabel
                  control={<Switch />}
                  label='Clean Titles Only'
                  onChange={() => {
                    if (cleanTitle == null) {
                      dispatch(thunks?.changeCleanTitle(true));
                      return;
                    } else {
                      dispatch(thunks?.changeCleanTitle(!cleanTitle));
                      return;
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <button onClick={() => handleSubmit()} className='primary mb-10'>
        Filter
      </button>
      <button
        onClick={() => handleResetFilter()}
        className='reset'
        disabled={!isFilter}
      >
        Reset Filter
      </button>
    </div>
  );
};

export default Filters;
